/**
 * Cicha Pewność w palecie marki klienta - routing zapewnia użytkownikowi prosty dostęp do spotkania w kancelarii, rozwinięć treści i ustawień prywatności.
 */
import { lazy, Suspense, type ComponentType } from "react";
import { Route, Switch, type RouteComponentProps } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home, { type HomeSsrSections } from "./pages/Home";
import { CookieConsent } from "./components/site/CookieConsent";
const LazyCampaignKomornik = lazy(() => import("./pages/CampaignKomornik"));
const LazyLocalOfficePage = lazy(() => import("./pages/LocalOfficePage"));
const LazyHowItWorksPage = lazy(async () => ({
  default: (await import("./pages/ServicePages")).HowItWorksPage,
}));
const LazyPricingPage = lazy(async () => ({
  default: (await import("./pages/ServicePages")).PricingPage,
}));
const LazyOfficesPage = lazy(async () => ({
  default: (await import("./pages/ServicePages")).OfficesPage,
}));
const LazyFaqPage = lazy(async () => ({
  default: (await import("./pages/ServicePages")).FaqPage,
}));
const LazyContactPage = lazy(async () => ({
  default: (await import("./pages/ServicePages")).ContactPage,
}));
const LazyCaseStudyPage = lazy(async () => ({
  default: (await import("./pages/ServicePages")).CaseStudyPage,
}));
const LazyThankYouMeetingPage = lazy(async () => ({
  default: (await import("./pages/ThankYouPages")).ThankYouMeetingPage,
}));
const LazyThankYouContactPage = lazy(async () => ({
  default: (await import("./pages/ThankYouPages")).ThankYouContactPage,
}));
const LazyPrivacyCookiesPage = lazy(() => import("./pages/PrivacyCookiesPage"));
const LazyTermsPage = lazy(() => import("./pages/TermsPage"));
const LazyNotFound = lazy(() => import("./pages/NotFound"));
type SsrRouteComponents = {
  home: ComponentType<RouteComponentProps>;
  campaign: ComponentType<RouteComponentProps>;
  localOffice: ComponentType<RouteComponentProps>;
  howItWorks: ComponentType<RouteComponentProps>;
  pricing: ComponentType<RouteComponentProps>;
  offices: ComponentType<RouteComponentProps>;
  faq: ComponentType<RouteComponentProps>;
  contact: ComponentType<RouteComponentProps>;
  caseStudy: ComponentType<RouteComponentProps>;
  thankYouMeeting: ComponentType<RouteComponentProps>;
  thankYouContact: ComponentType<RouteComponentProps>;
  privacy: ComponentType<RouteComponentProps>;
  terms: ComponentType<RouteComponentProps>;
  notFound: ComponentType<RouteComponentProps>;
};

function Router({ ssrPages }: { ssrPages?: SsrRouteComponents }) {
  const HomePage: ComponentType<RouteComponentProps> =
    ssrPages?.home ?? (Home as ComponentType<RouteComponentProps>);
  const CampaignKomornik = ssrPages?.campaign ?? LazyCampaignKomornik;
  const LocalOfficePage = ssrPages?.localOffice ?? LazyLocalOfficePage;
  const HowItWorksPage = ssrPages?.howItWorks ?? LazyHowItWorksPage;
  const PricingPage = ssrPages?.pricing ?? LazyPricingPage;
  const OfficesPage = ssrPages?.offices ?? LazyOfficesPage;
  const FaqPage = ssrPages?.faq ?? LazyFaqPage;
  const ContactPage = ssrPages?.contact ?? LazyContactPage;
  const CaseStudyPage = ssrPages?.caseStudy ?? LazyCaseStudyPage;
  const ThankYouMeetingPage =
    ssrPages?.thankYouMeeting ?? LazyThankYouMeetingPage;
  const ThankYouContactPage =
    ssrPages?.thankYouContact ?? LazyThankYouContactPage;
  const PrivacyCookiesPage = ssrPages?.privacy ?? LazyPrivacyCookiesPage;
  const TermsPage = ssrPages?.terms ?? LazyTermsPage;
  const NotFound = ssrPages?.notFound ?? LazyNotFound;
  return (
    <Suspense
      fallback={
        <div className="route-loader" aria-busy="true">
          <span className="sr-only">Ładowanie strony</span>
        </div>
      }
    >
      <Switch>
        <Route path="/" component={HomePage} />
        <Route path="/jak-to-dziala" component={HowItWorksPage} />
        <Route path="/cennik" component={PricingPage} />
        <Route path="/kancelarie/:city" component={LocalOfficePage} />
        <Route path="/kancelarie" component={OfficesPage} />
        <Route path="/faq" component={FaqPage} />
        <Route path="/kontakt" component={ContactPage} />
        <Route path="/case-study/61-milionow" component={CaseStudyPage} />
        <Route path="/dziekujemy-spotkanie" component={ThankYouMeetingPage} />
        <Route path="/dziekujemy-kontakt" component={ThankYouContactPage} />
        <Route
          path="/polityka-prywatnosci-rodo-i-cookies"
          component={PrivacyCookiesPage}
        />
        <Route
          path="/polityka-prywatnosci-i-cookies"
          component={PrivacyCookiesPage}
        />
        <Route path="/polityka-prywatnosci" component={PrivacyCookiesPage} />
        <Route path="/polityka-cookies" component={PrivacyCookiesPage} />
        <Route path="/regulamin" component={TermsPage} />
        <Route path="/lp/komornik" component={CampaignKomornik} />
        <Route path="/:legacyPath" component={LocalOfficePage} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}
function App({ ssrPages }: { ssrPages?: SsrRouteComponents }) {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <Router ssrPages={ssrPages} />
        <CookieConsent />
      </ThemeProvider>
    </ErrorBoundary>
  );
}
export default App;
